import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Grid } from "@mui/material";
import TabContext from "@mui/lab/TabContext";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import { Icon } from "@iconify/react";
import Layout from "../../../components/Layout";
import Button from "../../../components/Button";
import Modal from "../../../components/Modal";
import InputGroup from "../../../components/FormComponent/InputGroup";
import SelectGroup from "../../../components/FormComponent/SelectGroup";
import TextAreaGroup from "../../../components/FormComponent/TextAreaGroup";
import BreadCrumb from "../../../components/BreadCrumb";
import useStyles from "../styles";
import { httprequest } from "../../../data/api";
import { toast } from "react-toastify";
import useValidator from "../../../hooks/useValidator";
import FileDropZone from "./components/FileDropZone";
import MaterialPreviewModal from "../components/MaterialPreviewModal";
import uploadFile, { formatFileSize, getFileMeta } from "./utils/uploadFile";

const SYLLABUS_ENDPOINT = "/api/v1/syllabus";
const SESSION_ENDPOINT = "/api/v1/school-plan/session";
const TERM_ENDPOINT = "/api/v1/school-plan/terms";
const CLASSES_ENDPOINT = "/api/v1/class";

const SyllabusEditor = () => {
  const { subjectId, syllabusId } = useParams();
  const navigate = useNavigate();
  const classes = useStyles();

  const [syllabus, setSyllabus] = useState(null);
  const [selectedWeekId, setSelectedWeekId] = useState(null);
  const [tab, setTab] = useState("syllabus");
  const [isLoading, setIsLoading] = useState(false);

  const [materials, setMaterials] = useState([]);
  const [isMaterialsLoading, setIsMaterialsLoading] = useState(false);
  const [isAddMaterialModalOpen, setIsAddMaterialModalOpen] = useState(false);
  const [materialFiles, setMaterialFiles] = useState([]);
  const [isUploadingMaterial, setIsUploadingMaterial] = useState(false);
  const [previewMaterial, setPreviewMaterial] = useState(null);

  const [isTopicModalOpen, setIsTopicModalOpen] = useState(false);
  const [editingTopic, setEditingTopic] = useState(null);
  const [topicForm, setTopicForm] = useState({ title: "", objective: "", status: "DRAFT" });
  const [topicFiles, setTopicFiles] = useState([]);
  const [isSavingTopic, setIsSavingTopic] = useState(false);
  const [topicValidator, showTopicValidator] = useValidator();

  const [isWeekModalOpen, setIsWeekModalOpen] = useState(false);
  const [editingWeek, setEditingWeek] = useState(null);
  const [weekForm, setWeekForm] = useState({ title: "" });
  const [isSavingWeek, setIsSavingWeek] = useState(false);

  const [isEditSyllabusOpen, setIsEditSyllabusOpen] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [allTerms, setAllTerms] = useState([]);
  const [classList, setClassList] = useState([]);
  const [editForm, setEditForm] = useState({ session_id: "", term_id: "", class_id: [] });
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const dragTopicRef = useRef(null);
  const dragWeekRef = useRef(null);

  const getSyllabus = () => {
    setIsLoading(true);
    httprequest.get(`${SYLLABUS_ENDPOINT}/${syllabusId}`)
      .then((res) => {
        const data = res?.data?.data;
        setSyllabus(data);
        setIsLoading(false);
        setSelectedWeekId((prev) => prev || data?.weeks?.[0]?.id);
      })
      .catch((err) => {
        setIsLoading(false);
        toast.error(err?.response?.data?.message);
      });
  };

  const getMaterials = () => {
    setIsMaterialsLoading(true);
    httprequest.get(`${SYLLABUS_ENDPOINT}/${syllabusId}/materials`)
      .then((res) => {
        setMaterials(res?.data?.data || []);
        setIsMaterialsLoading(false);
      })
      .catch((err) => {
        setIsMaterialsLoading(false);
        toast.error(err?.response?.data?.message);
      });
  };

  const getSessions = () => httprequest.get(SESSION_ENDPOINT).then((res) => setSessions(res?.data?.data || [])).catch((err) => toast.error(err?.response?.data?.message || "Failed to load sessions"));
  const getTerms = () => httprequest.get(TERM_ENDPOINT).then((res) => setAllTerms(res?.data?.data || [])).catch((err) => toast.error(err?.response?.data?.message || "Failed to load terms"));
  const getClasses = () => httprequest.get(CLASSES_ENDPOINT).then((res) => setClassList(res?.data?.data || [])).catch((err) => toast.error(err?.response?.data?.message || "Failed to load classes"));

  useEffect(() => {
    getSyllabus();
    getSessions();
    getTerms();
    getClasses();
  }, [syllabusId]);

  useEffect(() => {
    if (tab === "materials") getMaterials();
  }, [tab, syllabusId]);

  const selectedWeek = syllabus?.weeks?.find((w) => w?.id === selectedWeekId) || syllabus?.weeks?.[0];
  const filteredWeeks = syllabus?.weeks || [];

  const handleAddWeek = () => {
    httprequest.post(`${SYLLABUS_ENDPOINT}/${syllabusId}/weeks`, {})
      .then((res) => {
        toast.success(res?.data?.message || "Week added successfully");
        setSelectedWeekId(res?.data?.data?.id);
        getSyllabus();
      })
      .catch((err) => toast.error(err?.response?.data?.message));
  };

  const handleWeekDrop = (dropWeekId) => {
    const draggedWeekId = dragWeekRef.current;
    dragWeekRef.current = null;
    if (!draggedWeekId || draggedWeekId === dropWeekId) return;
    const dropIndex = syllabus?.weeks?.findIndex((w) => w?.id === dropWeekId);
    if (dropIndex === -1 || typeof dropIndex !== "number") return;
    httprequest.patch(`${SYLLABUS_ENDPOINT}/weeks/${draggedWeekId}/reorder`, { position: dropIndex + 1 })
      .then(() => getSyllabus())
      .catch((err) => toast.error(err?.response?.data?.message));
  };

  const handleTopicDrop = (dropIndex) => {
    const fromIndex = dragTopicRef.current;
    dragTopicRef.current = null;
    if (fromIndex === null || fromIndex === dropIndex) return;
    const topic = selectedWeek?.topics?.[fromIndex];
    if (!topic) return;
    httprequest.patch(`${SYLLABUS_ENDPOINT}/topics/${topic.id}/reorder`, { position: dropIndex + 1 })
      .then(() => getSyllabus())
      .catch((err) => toast.error(err?.response?.data?.message));
  };

  const openAddTopicModal = () => {
    setEditingTopic(null);
    setTopicForm({ title: "", objective: "", status: "DRAFT" });
    setTopicFiles([]);
    setIsTopicModalOpen(true);
  };

  const openEditTopicModal = (topic) => {
    setEditingTopic(topic);
    setTopicForm({ title: topic?.title || "", objective: topic?.objective || "", status: topic?.status || "DRAFT" });
    setTopicFiles([]);
    setIsTopicModalOpen(true);
  };

  const closeTopicModal = () => {
    setIsTopicModalOpen(false);
    setEditingTopic(null);
    setTopicForm({ title: "", objective: "", status: "DRAFT" });
    setTopicFiles([]);
  };

  const handleSaveTopic = async () => {
    if (!topicValidator.allValid()) {
      showTopicValidator(true);

      return;
    }
    setIsSavingTopic(true);
    try {
      if (editingTopic) {
        const res = await httprequest.patch(`${SYLLABUS_ENDPOINT}/topics/${editingTopic.id}`, {
          title: topicForm.title,
          objective: topicForm.objective,
          status: topicForm.status
        });
        toast.success(res?.data?.message || "Topic updated successfully");
      } else {
        const uploadedMaterials = await Promise.all(topicFiles.map((file) => uploadFile(file)));
        const res = await httprequest.post(`${SYLLABUS_ENDPOINT}/weeks/${selectedWeekId}/topic`, {
          title: topicForm.title,
          objective: topicForm.objective,
          status: topicForm.status,
          materials: uploadedMaterials
        });
        toast.success(res?.data?.message || "Topic created successfully");
      }
      setIsSavingTopic(false);
      closeTopicModal();
      getSyllabus();
    } catch (err) {
      setIsSavingTopic(false);
      toast.error(err?.response?.data?.message);
    }
  };

  const openEditWeekModal = (week) => {
    setEditingWeek(week);
    setWeekForm({ title: week?.title || "" });
    setIsWeekModalOpen(true);
  };

  const closeWeekModal = () => {
    setIsWeekModalOpen(false);
    setEditingWeek(null);
    setWeekForm({ title: "" });
  };

  const handleSaveWeek = async () => {
    if (!weekForm.title) {
      toast.error("Title is required");

      return;
    }
    setIsSavingWeek(true);
    try {
      const res = await httprequest.patch(`${SYLLABUS_ENDPOINT}/weeks/${editingWeek.id}`, {
        title: weekForm.title
      });
      toast.success(res?.data?.message || "Week updated successfully");
      setIsSavingWeek(false);
      closeWeekModal();
      getSyllabus();
    } catch (err) {
      setIsSavingWeek(false);
      toast.error(err?.response?.data?.message || "Failed to update week");
    }
  };

  const handleTogglePublish = () => {
    const nextStatus = syllabus?.status === "PUBLISHED" ? "DRAFT" : "PUBLISHED";
    httprequest.patch(`${SYLLABUS_ENDPOINT}/${syllabusId}`, { status: nextStatus })
      .then((res) => {
        toast.success(res?.data?.message || "Syllabus updated successfully");
        getSyllabus();
      })
      .catch((err) => toast.error(err?.response?.data?.message));
  };

  const handleSaveChanges = () => {
    toast.success("Changes saved");
  };

  const openAddMaterialModal = () => {
    setMaterialFiles([]);
    setIsAddMaterialModalOpen(true);
  };

  const closeAddMaterialModal = () => {
    setMaterialFiles([]);
    setIsAddMaterialModalOpen(false);
  };

  const handleUploadMaterials = async () => {
    if (materialFiles.length === 0) return;
    setIsUploadingMaterial(true);
    try {
      await Promise.all(materialFiles.map(async (file) => {
        const uploaded = await uploadFile(file);

        return httprequest.post(`${SYLLABUS_ENDPOINT}/${syllabusId}/materials`, uploaded);
      }));
      toast.success("Material(s) added successfully");
      setIsUploadingMaterial(false);
      closeAddMaterialModal();
      getMaterials();
    } catch (err) {
      setIsUploadingMaterial(false);
      toast.error(err?.response?.data?.message);
    }
  };

  const openEditSyllabusModal = () => {
    setEditForm({ session_id: syllabus?.session?.id || "", term_id: syllabus?.term?.id || "", class_id: [] });
    if (sessions.length === 0) getSessions();
    if (allTerms.length === 0) getTerms();
    if (classList.length === 0) getClasses();
    setIsEditSyllabusOpen(true);
  };

  const toggleEditClass = (classId) => {
    setEditForm((prev) => ({
      ...prev,
      class_id: prev.class_id.includes(classId)
        ? prev.class_id.filter((id) => id !== classId)
        : [...prev.class_id, classId]
    }));
  };

  const handleRemoveAssignedClass = (classId) => {
    httprequest.delete(`${SYLLABUS_ENDPOINT}/${syllabusId}/classes/${classId}`)
      .then(() => {
        toast.success("Class removed from syllabus");
        getSyllabus();
      })
      .catch((err) => toast.error(err?.response?.data?.message));
  };

  const handleSaveEditSyllabus = () => {
    setIsSavingEdit(true);
    const existingIds = syllabus?.classes?.map((c) => c?.id) || [];
    const mergedClasses = Array.from(new Set([...existingIds, ...editForm.class_id]));
    httprequest.patch(`${SYLLABUS_ENDPOINT}/${syllabusId}`, {
      session_id: editForm.session_id,
      term_id: editForm.term_id,
      classes: mergedClasses
    })
      .then((res) => {
        setIsSavingEdit(false);
        toast.success(res?.data?.message || "Syllabus updated successfully");
        setIsEditSyllabusOpen(false);
        getSyllabus();
      })
      .catch((err) => {
        setIsSavingEdit(false);
        toast.error(err?.response?.data?.message);
      });
  };

  const confirmDelete = (type, id) => setDeleteTarget({ type, id });

  const handleConfirmDelete = () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    let url = "";
    if (deleteTarget.type === "week") url = `${SYLLABUS_ENDPOINT}/weeks/${deleteTarget.id}`;
    else if (deleteTarget.type === "topic") url = `${SYLLABUS_ENDPOINT}/topics/${deleteTarget.id}`;
    else if (deleteTarget.type === "material") url = `${SYLLABUS_ENDPOINT}/${syllabusId}/materials/${deleteTarget.id}`;

    httprequest.delete(url)
      .then((res) => {
        setIsDeleting(false);
        toast.success(res?.data?.message || "Deleted successfully");
        const wasMaterial = deleteTarget.type === "material";
        setDeleteTarget(null);
        if (wasMaterial) getMaterials(); else getSyllabus();
      })
      .catch((err) => {
        setIsDeleting(false);
        toast.error(err?.response?.data?.message);
      });
  };

  const termsForEdit = allTerms.filter((term) => !editForm.session_id || term?.session_id === editForm.session_id);
  const assignableClasses = classList.filter((c) => !syllabus?.classes?.some((existing) => existing?.id === c?.id));
  const classNames = syllabus?.classes?.map((c) => c?.class_name) || [];
  const classSummary = classNames.length > 2 ? `${classNames.slice(0, 2).join(", ")}, +${classNames.length - 2}` : classNames.join(", ");

  return (
    <Layout
      children={
        <>
          <BreadCrumb
            link_one={`/class-activities/employer/syllabus/${subjectId}`}
            link_one_name="Syllabus"
            active="Syllabus Details"
          />

          {!isLoading && syllabus && (
            <div style={{ marginTop: "20px" }}>
              <div className={`${classes.editorHeader} white_bg`}>
                <div style={{ display: "flex", gap: 12 }}>
                  <div className={classes.weekBadge} style={{ width: 44, height: 44, borderRadius: 12, fontSize: 15 }}>
                    {syllabus?.title?.slice(0, 2)?.toUpperCase()}
                  </div>
                  <div style={{ textAlign: "left" }}>
                    <div className={classes.editorHeaderInfo}>
                      <h4 style={{ margin: 0 }}>{syllabus?.title}</h4>
                      <span className={`${classes.statusBadge} ${syllabus?.status === "PUBLISHED" ? classes.statusBadgePublished : classes.statusBadgeDraft}`}>
                        {syllabus?.status === "PUBLISHED" ? "Published" : "Draft"}
                      </span>
                    </div>
                    <div className={classes.editorHeaderInfo} style={{ marginTop: 10 }}>
                      <span className={classes.metaChip}>
                        <Icon icon="mdi:calendar-blank-outline" /> {syllabus?.session?.name} Session
                      </span>
                      <span className={classes.metaChip}>{syllabus?.term?.name}</span>
                      <span className={classes.metaChip}>
                        <Icon icon="mdi:google-classroom" /> {classNames.length} classes ({classSummary})
                      </span>
                    </div>
                  </div>
                </div>
                <Button
                  buttonName="Edit Syllabus"
                  buttonIcon={<Icon icon="heroicons:pencil-square-solid" style={{ marginRight: 6 }} />}
                  variant="primaryoutline"
                  color="btnfontprimary"
                  buttonSize="fluid"
                  onClick={openEditSyllabusModal}
                />
              </div>

              <TabContext value={tab}>
                <div className="tablist-container padding-left-16">
                  <Tabs className="overide-tablist" onChange={(e, v) => setTab(v)} aria-label="syllabus editor tabs">
                    <Tab label="Syllabus" value="syllabus" />
                    <Tab label="Materials" value="materials" />
                  </Tabs>
                </div>

                <TabPanel value="syllabus" className="tabpanel-overide">
                  <div className="white_bg">
                    <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 16 }}>
                      {/* Filters removed per request */}
                    </div>
                    <div className={`${classes.editorBody}`}>
                      <div className={classes.weekSidebar}>
                        <p style={{ fontSize: 12, fontWeight: 600, color: "#98A2B3", letterSpacing: 0.5, margin: 0 }}>
                          {syllabus?.term?.name?.toUpperCase()}
                        </p>
                        {filteredWeeks.map((week) => (
                          <div
                            key={week?.id}
                            draggable
                            onDragStart={() => (dragWeekRef.current = week?.id)}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={() => handleWeekDrop(week?.id)}
                            className={`${classes.weekItem} ${selectedWeekId === week?.id ? classes.weekItemActive : ""}`}
                            onClick={() => setSelectedWeekId(week?.id)}
                          >
                            <span className={classes.weekBadge}>W{week?.week}</span>
                            <div>
                              <p className={classes.weekItemTitle}>{week?.title}</p>
                              <p className={classes.weekItemSubtitle}>{week?.topics?.length || 0} Topics</p>
                            </div>
                          </div>
                        ))}
                        <Button
                          buttonName="Add Week"
                          buttonIcon={<Icon icon="mdi:plus" style={{ marginRight: 6 }} />}
                          variant="primaryoutline"
                          color="btnfontprimary"
                          buttonSize="fluid"
                          onClick={handleAddWeek}
                        />
                      </div>

                      <div className={classes.weekPanel}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                          <div>
                            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                              <h4 style={{ margin: 0, textAlign: "left" }}>{selectedWeek?.title || "Select a week"}</h4>
                              {selectedWeek && (
                                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                  <Icon
                                    icon="heroicons:pencil-square-solid"
                                    style={{ cursor: "pointer", color: "#667085", fontSize: 18 }}
                                    onClick={() => openEditWeekModal(selectedWeek)}
                                  />
                                  <Icon
                                    icon="mingcute:delete-2-line"
                                    style={{ cursor: "pointer", color: "#F04438", fontSize: 18 }}
                                    onClick={() => confirmDelete("week", selectedWeek?.id)}
                                  />
                                </div>
                              )}
                            </div>
                            <p style={{ margin: 0, color: "#667085", fontSize: 13, textAlign: "left", marginTop: 4 }}>
                              {selectedWeek?.title} . {selectedWeek?.topics?.length || 0} Topics
                            </p>
                          </div>
                          <Button
                            buttonName="Add Topic"
                            buttonIcon={<Icon icon="mdi:plus" style={{ marginRight: 6 }} />}
                            variant="primary"
                            color="btndefault"
                            buttonSize="fluid"
                            onClick={openAddTopicModal}
                            disabled={!selectedWeekId}
                          />
                        </div>

                        {(!selectedWeek || (selectedWeek?.topics || []).length === 0) ? (
                          <div className={classes.emptyStateContainer} style={{ border: "1px dashed #D0D5DD", borderRadius: 10 }}>
                            <p style={{ margin: 0, color: "#667085" }}>No topics in this week yet. Add the first one.</p>
                          </div>
                        ) : (
                          selectedWeek.topics.map((topic, index) => (
                            <div
                              key={topic?.id}
                              draggable
                              onDragStart={() => (dragTopicRef.current = index)}
                              onDragOver={(e) => e.preventDefault()}
                              onDrop={() => handleTopicDrop(index)}
                              className={classes.topicRow}
                            >
                              <Icon icon="mdi:drag" style={{ color: "#98A2B3", cursor: "grab", fontSize: 20 }} />
                              <div
                                style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 4, cursor: "pointer" }}
                                onClick={() => navigate(`/class-activities/employer/syllabus/${subjectId}/${syllabusId}/topics/${topic?.id}`)}
                              >
                                <p className={classes.topicTitle}>{topic?.title}</p>
                                <p className={classes.topicObjective}>{topic?.objective || "No objective set"}</p>
                              </div>
                              {topic?.status === "PUBLISHED" && topic?.progress === "COMPLETED" ? (
                                <span className={`${classes.progressBadge} ${classes.progressCompleted}`}>
                                  Completed
                                </span>
                              ) : topic?.status === "PUBLISHED" && topic?.progress === "IN_PROGRESS" ? (
                                <span className={`${classes.progressBadge} ${classes.progressInProgress}`}>
                                  In Progress
                                </span>
                              ) : (
                                <span className={`${classes.progressBadge} ${classes.progressNotStarted}`}>
                                  {topic?.status === "PUBLISHED" ? "Not Started" : "Draft"}
                                </span>
                              )}
                              <div className={classes.topicActions}>
                                <button className={classes.topicActionButton} onClick={(e) => {
                                  e.stopPropagation(); confirmDelete("topic", topic?.id);
                                }}
                                >
                                  <Icon icon="mingcute:delete-2-line" />
                                </button>
                                <button className={classes.topicActionButton} onClick={(e) => {
                                  e.stopPropagation(); openEditTopicModal(topic);
                                }}
                                >
                                  <Icon icon="heroicons:pencil-square-solid" />
                                </button>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>

                    <div className={classes.modalFooterBtn} style={{ marginTop: 24 }}>
                      <Button buttonName="Cancel" variant="primaryoutline" color="btnfontprimary" buttonSize="fluid" onClick={() => navigate(-1)} />
                      <Button
                        buttonName={syllabus?.status === "PUBLISHED" ? "Save Changes" : "Save as Draft"}
                        variant="primaryoutline"
                        color="btnfontprimary"
                        buttonSize="fluid"
                        onClick={handleSaveChanges}
                      />
                      <Button
                        buttonName={syllabus?.status === "PUBLISHED" ? "Unpublish" : "Publish"}
                        variant="primary"
                        color="btndefault"
                        buttonSize="fluid"
                        onClick={handleTogglePublish}
                      />
                    </div>
                  </div>
                </TabPanel>

                <TabPanel value="materials" className="tabpanel-overide">
                  {materials.length === 0 && !isMaterialsLoading ? (
                    <div className={classes.emptyStateContainer}>
                      <div className={classes.emptyStateIcon}><Icon icon="mdi:folder-outline" /></div>
                      <h3 className={classes.emptyStateTitle}>No materials yet</h3>
                      <p className={classes.emptyStateSubtitle}>
                        Upload documents, presentations, videos, and other learning resources to support
                        your teaching and keep students engaged.
                      </p>
                      <Button
                        buttonName="Add Material"
                        buttonIcon={<Icon icon="mdi:plus" style={{ marginRight: 6 }} />}
                        variant="primary"
                        color="btndefault"
                        buttonSize="fluid"
                        onClick={openAddMaterialModal}
                      />
                    </div>
                  ) : (
                    <>
                      <div className={`${classes.materialsGrid} white_bg`}>
                        {materials.map((material) => {
                          const meta = getFileMeta(material?.file_name);

                          return (
                            <div key={material?.id} className={classes.materialCard} onClick={() => setPreviewMaterial(material)}>
                              <div className={classes.materialIcon} style={{ background: meta.bg, color: meta.color }}>
                                <Icon icon={meta.icon} />
                              </div>
                              <div style={{ minWidth: 0 }}>
                                <p className={classes.materialTitle}>{material?.title}</p>
                                <p className={classes.materialMeta}>{meta.label} . {formatFileSize(material?.file_size)}</p>
                              </div>
                              <div className={classes.materialActions}>
                                <Icon icon="mingcute:delete-2-line" onClick={(e) => {
                                  e.stopPropagation();
                                  confirmDelete("material", material?.id);
                                }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <div style={{ marginTop: 20, display: "flex", justifyContent: "flex-end" }}>
                        <Button
                          buttonName="Add Materials"
                          buttonIcon={<Icon icon="mdi:plus" style={{ marginRight: 6 }} />}
                          variant="primary"
                          color="btndefault"
                          buttonSize="fluid"
                          onClick={openAddMaterialModal}
                        />
                      </div>
                    </>
                  )}
                </TabPanel>
              </TabContext>
            </div>
          )}

          {/* Add / Edit Topic Modal */}
          <Modal
            title={editingTopic ? "Edit Topic" : "Add New Topic"}
            subtitle={<div>{editingTopic ? "Edit this topic" : "Add new topic"}</div>}
            modalContent={
              <Grid container spacing={2} className="input-padding">
                <Grid item xs={12}>
                  <InputGroup
                    label="Topic"
                    required
                    placeHolder="Enter topic title"
                    inputType="text"
                    value={topicForm.title}
                    onChange={(e) => setTopicForm((prev) => ({ ...prev, title: e.target.value }))}
                  />
                  {topicValidator.message("Topic", topicForm.title, "required")}
                </Grid>
                <Grid item xs={12}>
                  <TextAreaGroup
                    label="Objective"
                    placeHolder="What will students learn from this topic"
                    value={topicForm.objective}
                    onChange={(e) => setTopicForm((prev) => ({ ...prev, objective: e.target.value }))}
                  />
                </Grid>
                {!editingTopic && (
                  <Grid item xs={12}>
                    <SelectGroup label="Attachments">
                      <FileDropZone
                        files={topicFiles}
                        onFilesSelected={(files) => setTopicFiles((prev) => [...prev, ...files])}
                        onRemoveFile={(index) => setTopicFiles((prev) => prev.filter((_, i) => i !== index))}
                        subtitle="Attach material (PDF, slide, link)"
                      />
                    </SelectGroup>
                  </Grid>
                )}
                {/* Status select group removed as requested, defaults to DRAFT */}
              </Grid>
            }
            modalFooter={
              <div className={classes.modalFooterBtn}>
                <Button buttonName="Cancel" onClick={closeTopicModal} variant="primaryoutline" color="btnfontprimary" buttonSize="fluid" />
                <Button
                  buttonName={editingTopic ? "Save Changes" : "Add Topic"}
                  variant="primary"
                  color="btndefault"
                  buttonSize="fluid"
                  onClick={handleSaveTopic}
                  isLoading={isSavingTopic}
                />
              </div>
            }
            isopen={isTopicModalOpen}
            closeModal={closeTopicModal}
          />

          {/* Add Material Modal */}
          <Modal
            title="Add Material"
            subtitle={<div>Upload material for this subject</div>}
            modalContent={
              <FileDropZone
                files={materialFiles}
                onFilesSelected={(files) => setMaterialFiles((prev) => [...prev, ...files])}
                onRemoveFile={(index) => setMaterialFiles((prev) => prev.filter((_, i) => i !== index))}
              />
            }
            modalFooter={
              <div className={classes.modalFooterBtn}>
                <Button buttonName="Cancel" onClick={closeAddMaterialModal} variant="primaryoutline" color="btnfontprimary" buttonSize="fluid" />
                <Button
                  buttonName="Add Material"
                  variant="primary"
                  color="btndefault"
                  buttonSize="fluid"
                  onClick={handleUploadMaterials}
                  isLoading={isUploadingMaterial}
                  disabled={materialFiles.length === 0}
                />
              </div>
            }
            isopen={isAddMaterialModalOpen}
            closeModal={closeAddMaterialModal}
          />

          {/* Edit Syllabus Modal */}
          <Modal
            title="Edit Syllabus"
            subtitle={<div>A syllabus is created once per subject and assigned to one or more classes.</div>}
            modalContent={
              <Grid container spacing={2} className="input-padding">
                <Grid item xs={12} sm={6}>
                  <SelectGroup label="Session">
                    <select
                      value={editForm.session_id}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, session_id: e.target.value, term_id: "" }))}
                    >
                      <option value="">Select Session</option>
                      {sessions.map((s) => (
                        <option key={s?.id} value={s?.id}>{s?.session}</option>
                      ))}
                    </select>
                  </SelectGroup>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SelectGroup label="Term">
                    <select
                      value={editForm.term_id}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, term_id: e.target.value }))}
                    >
                      <option value="">Select Term</option>
                      {termsForEdit.map((t) => (
                        <option key={t?.id} value={t?.id}>{t?.term}</option>
                      ))}
                    </select>
                  </SelectGroup>
                </Grid>
                <Grid item xs={12}>
                  <SelectGroup label="Assign Classes (Select one or more)">
                    <div className={classes.checkboxList}>
                      {assignableClasses.map((c) => (
                        <label key={c?.id} className={classes.checkboxItem}>
                          <input
                            type="checkbox"
                            checked={editForm.class_id.includes(c?.id)}
                            onChange={() => toggleEditClass(c?.id)}
                          />
                          {c?.class_name}
                        </label>
                      ))}
                    </div>
                  </SelectGroup>
                </Grid>
                <Grid item xs={12}>
                  <SelectGroup label="Assigned Classes (Select one or more)">
                    <div className={classes.chipList}>
                      {syllabus?.classes?.map((c) => (
                        <span key={c?.id} className={classes.chip}>
                          {c?.class_name}
                          <Icon
                            icon="mdi:close"
                            className={classes.chipRemove}
                            onClick={() => handleRemoveAssignedClass(c?.id)}
                          />
                        </span>
                      ))}
                    </div>
                  </SelectGroup>
                </Grid>
              </Grid>
            }
            modalFooter={
              <div className={classes.modalFooterBtn}>
                <Button buttonName="Cancel" onClick={() => setIsEditSyllabusOpen(false)} variant="primaryoutline" color="btnfontprimary" buttonSize="fluid" />
                <Button
                  buttonName="Save Edit"
                  variant="primary"
                  color="btndefault"
                  buttonSize="fluid"
                  onClick={handleSaveEditSyllabus}
                  isLoading={isSavingEdit}
                />
              </div>
            }
            isopen={isEditSyllabusOpen}
            closeModal={() => setIsEditSyllabusOpen(false)}
          />

          {/* Edit Week Modal */}
          <Modal
            title="Edit Week"
            subtitle={<div>Edit week details</div>}
            modalContent={
              <Grid container spacing={2} className="input-padding">
                <Grid item xs={12}>
                  <InputGroup
                    label="Week Title"
                    required
                    placeHolder="Enter week title"
                    inputType="text"
                    value={weekForm.title}
                    onChange={(e) => setWeekForm({ ...weekForm, title: e.target.value })}
                  />
                </Grid>
              </Grid>
            }
            modalFooter={
              <div className={classes.modalFooterBtn}>
                <Button buttonName="Cancel" onClick={closeWeekModal} variant="primaryoutline" color="btnfontprimary" buttonSize="fluid" />
                <Button
                  buttonName="Save Changes"
                  variant="primary"
                  color="btndefault"
                  buttonSize="fluid"
                  onClick={handleSaveWeek}
                  isLoading={isSavingWeek}
                />
              </div>
            }
            isopen={isWeekModalOpen}
            closeModal={closeWeekModal}
          />

          {/* Delete confirmation */}
          <Modal
            title="Confirm Delete"
            subtitle={<div>Are you sure you want to delete this {deleteTarget?.type}? This action cannot be undone.</div>}
            modalContent={<div />}
            modalFooter={
              <div className={classes.modalFooterBtn}>
                <Button buttonName="Cancel" onClick={() => setDeleteTarget(null)} variant="primaryoutline" color="btnfontprimary" buttonSize="fluid" />
                <Button
                  buttonName="Delete"
                  variant="danger"
                  color="btndefault"
                  buttonSize="fluid"
                  onClick={handleConfirmDelete}
                  isLoading={isDeleting}
                />
              </div>
            }
            isopen={Boolean(deleteTarget)}
            closeModal={() => setDeleteTarget(null)}
          />

          {/* Preview Material Modal */}
          <MaterialPreviewModal
            isOpen={!!previewMaterial}
            closeModal={() => setPreviewMaterial(null)}
            material={previewMaterial}
          />
        </>
      }
    />
  );
};

export default SyllabusEditor;
